import { PaginationInfo } from "@types";
import { TFunction } from "i18next";
import { useLayoutEffect, useRef } from "react";

type PaginationButton = "previous" | "next";

interface PaginationNavProps {
  pagination: PaginationInfo;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  t: TFunction<"translation", undefined>;
}

export function PaginationNav({
  pagination,
  isLoading,
  onPageChange,
  t,
}: PaginationNavProps) {
  const { current_page: currentPage, total_pages: totalPages } = pagination;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const activated = useRef<PaginationButton | null>(null);

  useLayoutEffect(() => {
    const active = document.activeElement;
    const focusLost = !active || active === document.body;
    const previous = previousRef.current;
    const next = nextRef.current;
    if (activated.current === "next" && next?.disabled && previous) {
      if (focusLost || active === next) previous.focus();
    }
    if (activated.current === "previous" && previous?.disabled && next) {
      if (focusLost || active === previous) next.focus();
    }
    activated.current = null;
  }, [currentPage, totalPages]);

  const handleBlur = () => {
    activated.current = null;
  };

  const activate = (button: PaginationButton, targetPage: number) => {
    if (isLoading) return;
    activated.current = button;
    onPageChange(targetPage);
  };

  return (
    <nav aria-label={t("pagination")}>
      <button
        ref={previousRef}
        type="button"
        onClick={() => activate("previous", currentPage - 1)}
        onBlur={handleBlur}
        disabled={isFirstPage}
        aria-disabled={isLoading || undefined}
        aria-label={
          isFirstPage
            ? t("paginationPreviousEdgeName")
            : t("paginationPreviousName", { page: currentPage - 1 })
        }
        className="min-h-target min-w-target disabled:cursor-not-allowed mr-2 text-primary"
      >
        {t("paginationPrev")}
      </button>
      <button
        ref={nextRef}
        type="button"
        onClick={() => activate("next", currentPage + 1)}
        onBlur={handleBlur}
        disabled={isLastPage}
        aria-disabled={isLoading || undefined}
        aria-label={
          isLastPage
            ? t("paginationNextEdgeName")
            : t("paginationNextName", { page: currentPage + 1 })
        }
        className="min-h-target min-w-target disabled:cursor-not-allowed text-primary"
      >
        {t("paginationNext")}
      </button>
    </nav>
  );
}
